import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteRecommendation, getMyRecommendations } from '@/api/reco';
import type { MyRecommendationsResponse } from '@/types';

const myRecoKey = (q: string) => ['my-recommendations', q] as const;

/**
 * The current user's own recommendations, paginated (infinite scroll) and
 * filtered by an optional title/artist search term.
 */
export function useMyRecommendations(q: string) {
  return useInfiniteQuery<MyRecommendationsResponse, Error>({
    queryKey: myRecoKey(q),
    queryFn: ({ pageParam }) => getMyRecommendations(pageParam as number, q),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pageCount } = lastPage.meta.pagination;
      return page < pageCount ? page + 1 : undefined;
    },
  });
}

/** Delete one of the user's recommendations, dropping it from every cached page. */
export function useDeleteRecommendation(q: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (documentId) => deleteRecommendation(documentId),
    onSuccess: (_res, documentId) => {
      qc.setQueryData<{ pages: MyRecommendationsResponse[]; pageParams: unknown[] }>(
        myRecoKey(q),
        (prev) =>
          prev
            ? {
                ...prev,
                pages: prev.pages.map((p) => ({
                  ...p,
                  data: p.data.filter((r) => r.documentId !== documentId),
                })),
              }
            : prev,
      );
    },
  });
}
